import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  inject,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { API_MODE, TenantService } from '@vsolv/core/multi-tenant/web';
import { API_BASE_URL, PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { FIREBASE_AUTH, idToken$ } from '@vsolv/packages/firebase/web';
import { Payment } from '@vsolv/packages/payments/domain';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import {
  BadgeColumn,
  ConnectedTableComponent,
  ConnectedTableDataSourceQueryParams,
  TableColumn,
  TextColumn,
} from '@vsolv/vectors/table';
import { BatchPayment } from '@wsphere/warranties/domain';
import { Auth } from 'firebase/auth';
import moment from 'moment';
import { PAYMENT_HISTORY_CONFIG } from '../../config/payment-history.config';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  ReplaySubject,
  skip,
  Subscription,
} from 'rxjs';
import { PaymentHistoryDetailDialog } from '../../dialogs';

type DateRangeInput = Date | { startDate: Date | null; endDate: Date | null } | null;

interface HistoryFilters {
  status: BatchPayment.HistoryStatus | null;
  startDate: Date | null;
  endDate: Date | null;
}

const NO_FILTERS: HistoryFilters = { status: null, startDate: null, endDate: null };

const STATUS_THEME: Record<BatchPayment.HistoryStatus, ThemeColor> = {
  [BatchPayment.HistoryStatus.PAID]: 'success',
  [BatchPayment.HistoryStatus.PROCESSING]: 'warn',
  [BatchPayment.HistoryStatus.FAILED]: 'danger',
  [BatchPayment.HistoryStatus.REFUNDED]: 'info',
  [BatchPayment.HistoryStatus.DRAFT]: 'default',
  [BatchPayment.HistoryStatus.CANCELLED]: 'default',
  [BatchPayment.HistoryStatus.EXPIRED]: 'default',
};

function formatCents(cents: number | string | undefined) {
  return (Number(cents ?? 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// YYYY-MM-DD in the user's local timezone, matching how the warranty table sends dates.
function formatDate(date: Date | null | undefined): string | undefined {
  return date ? new Date(date).toLocaleDateString('en-CA') : undefined;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-distributor-payment-history-page',
  templateUrl: './payment-history.page.html',
  standalone: false,
})
export class DistributorPaymentHistoryPage implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(FIREBASE_AUTH) private auth: Auth,
    private tenantSvc: TenantService,
    @Inject(API_MODE) private apiMode: string
  ) {}

  private apiBaseUrl = inject(API_BASE_URL);

  @Input() distributorId: string | null = null;
  @PropertyListener('distributorId') private distributorId$ = new ReplaySubject<string | null>(1);

  @ViewChild(PaymentHistoryDetailDialog) private detailDialog?: PaymentHistoryDetailDialog;
  @ViewChild(ConnectedTableComponent) private table?: ConnectedTableComponent<BatchPayment.HistoryItem>;

  private pageResetSub?: Subscription;

  readonly config = PAYMENT_HISTORY_CONFIG;
  readonly statuses = Object.values(BatchPayment.HistoryStatus);
  readonly statusLabels = BatchPayment.HistoryStatusLabel;

  readonly filters$ = new BehaviorSubject<HistoryFilters>(NO_FILTERS);
  readonly search$ = new BehaviorSubject('');

  protected totalItems: number | null = null;

  protected endpoint$ = this.distributorId$.pipe(
    distinctUntilChanged(),
    map(distributorId => (distributorId ? `/api/distributors/${distributorId}/batch-payments` : null))
  );

  private debouncedSearch$ = this.search$.pipe(debounceTime(500), distinctUntilChanged());

  protected queryParams$ = combineLatest([this.filters$, this.debouncedSearch$]).pipe(
    map(([filters, search]) => {
      const params: ConnectedTableDataSourceQueryParams = {};

      if (filters.status) params['status'] = filters.status;

      const startDate = formatDate(filters.startDate);
      if (startDate) params['startDate'] = startDate;

      const endDate = formatDate(filters.endDate);
      if (endDate) params['endDate'] = endDate;

      const trimmed = search.trim();
      if (trimmed) params['search'] = trimmed;

      return params;
    })
  );

  protected hasFilters$ = combineLatest([this.filters$, this.search$]).pipe(
    map(([filters, search]) => !!(filters.status || filters.startDate || filters.endDate || search.trim()))
  );

  // An anchor download sends no headers, so the token, tenant and mode ride along as params.
  protected exportUrl$ = combineLatest([
    idToken$(this.auth),
    this.filters$,
    this.debouncedSearch$,
    this.distributorId$,
  ]).pipe(
    map(([token, filters, search, distributorId]) => {
      if (!distributorId) return null;

      const startDate = formatDate(filters.startDate);
      const endDate = formatDate(filters.endDate);
      const trimmed = search.trim();

      const params = [
        ...(filters.status ? [`status=${filters.status}`] : []),
        ...(startDate ? [`startDate=${startDate}`] : []),
        ...(endDate ? [`endDate=${endDate}`] : []),
        ...(trimmed ? [`search=${encodeURIComponent(trimmed)}`] : []),
        `tenantId=${this.tenantSvc.tenantId}`,
        `mode=${this.apiMode}`,
        `idToken=${token}`,
      ].join('&');

      return `${this.apiBaseUrl}/v1/admin-dashboard/distributors/${distributorId}/batch-payments/csv?${params}`;
    })
  );

  columns: TableColumn<unknown>[] = [
    new TextColumn<BatchPayment.HistoryItem>(
      { header: PAYMENT_HISTORY_CONFIG.columns.date, sortable: true, sort: 'DESC', fitContent: true },
      item => ({ text: moment(item.transactionDate ?? item.created).format('MMM DD, YYYY') })
    ),

    new TextColumn<BatchPayment.HistoryItem>({ header: PAYMENT_HISTORY_CONFIG.columns.reference }, item => ({
      text: item.referenceNumber ?? '—',
      classes: 'font-medium',
    })),

    new TextColumn<BatchPayment.HistoryItem>({ header: PAYMENT_HISTORY_CONFIG.columns.method }, item => ({
      text: item.paymentMethod ?? '—',
    })),

    new TextColumn<BatchPayment.HistoryItem>(
      { header: PAYMENT_HISTORY_CONFIG.columns.warranties, align: 'right', fitContent: true },
      item => ({ text: `${item.warrantyCount}` })
    ),

    new TextColumn<BatchPayment.HistoryItem>(
      { header: PAYMENT_HISTORY_CONFIG.columns.total, align: 'right', fitContent: true },
      item => ({ text: formatCents(item.totalAmount), classes: 'font-medium' })
    ),

    new BadgeColumn<BatchPayment.HistoryItem>(
      { header: PAYMENT_HISTORY_CONFIG.columns.status, fitContent: true },
      item => ({
        text:
          item.paymentStatus === Payment.Status.PARTIALLY_REFUNDED
            ? BatchPayment.PartiallyRefundedLabel
            : this.statusLabels[item.status],
        theme: STATUS_THEME[item.status],
      })
    ),
  ];

  // Narrowing filters from a deep page would strand the view past the last page, so query
  // changes reset to page 1. Initial emissions are skipped - only real changes reset.
  ngAfterViewInit() {
    this.pageResetSub = merge(this.queryParams$.pipe(skip(1)), this.endpoint$.pipe(skip(1))).subscribe(() =>
      this.table?.dataSource.setPage(1)
    );
  }

  // The table stays mounted while payments happen on other tabs - the parent refetches it on tab switch.
  refresh() {
    this.table?.dataSource?.refresh();
  }

  protected onPageLoaded(page: PaginationQueryResponse<BatchPayment.HistoryItem> | null) {
    this.totalItems = page?.meta?.totalItems ?? null;
  }

  protected openDetail(item: BatchPayment.HistoryItem) {
    this.detailDialog?.open(item);
  }

  protected onStatusChange(status: unknown) {
    this.updateFilters({ status: typeof status === 'string' ? (status as BatchPayment.HistoryStatus) : null });
  }

  protected onDateRangeChange(dateRange: DateRangeInput) {
    if (dateRange instanceof Date) {
      this.updateFilters({ startDate: dateRange, endDate: dateRange });
    } else if (dateRange && 'startDate' in dateRange) {
      this.updateFilters({ startDate: dateRange.startDate ?? null, endDate: dateRange.endDate ?? null });
    } else {
      this.updateFilters({ startDate: null, endDate: null });
    }
  }

  protected onSearchInput(search: string) {
    this.search$.next(search);
  }

  protected clearFilters() {
    this.filters$.next(NO_FILTERS);
    this.search$.next('');
  }

  private updateFilters(patch: Partial<HistoryFilters>) {
    this.filters$.next({ ...this.filters$.value, ...patch });
  }

  ngOnDestroy() {
    this.pageResetSub?.unsubscribe();
    this.filters$.complete();
    this.search$.complete();
    this.distributorId$.complete();
  }
}
